import React, { useRef, useState } from 'react';
import CallMadeIcon from '@mui/icons-material/CallMade';
import styles from './style.module.scss';
import { buildClassName } from 'utils/build-class-name';
import { useKeenSlider } from 'keen-slider/react';
import PlayButtonIcon from 'components/icon/play-button';
import Button, { PRIMARY_THEME, SECONDARY_THEME } from 'components/control/button';
import { ExprealtyUsThemeColors } from '@tenants/definitions/exp/commercial/theme-config';
import Link from 'next/link';

const NUM_SLIDES = 6;

type PlayButtonProps = {
  isPlaying: boolean;
  onClick: () => void;
};

const PlayButton = ({ isPlaying, onClick }: PlayButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <button
      id="playButton"
      data-testid="play-button"
      className={buildClassName(styles['play-button'], isPlaying && styles.invisible)}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <PlayButtonIcon
        foregroundFill={isHovered || isPlaying ? ExprealtyUsThemeColors.primary : ExprealtyUsThemeColors.primaryBackground}
        backgroundFill={isHovered || isPlaying ? ExprealtyUsThemeColors.primaryBackground : ExprealtyUsThemeColors.primary}
      />
    </button>
  );
};

type GetInTouchProps = {
  autoPlay?: boolean;
};

export default function GetInTouch({ autoPlay = false }: GetInTouchProps) {
  const animation = { duration: 16000, easing: (t: number) => t };
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [sliderRef] = useKeenSlider({
    loop: true,
    renderMode: 'performance',
    drag: false,
    slides: {
      perView: 'auto',
      spacing: 5,
    },
    created(s) {
      s.moveToIdx(NUM_SLIDES - 1, true, animation);
    },
    updated(s) {
      s.moveToIdx(s.track.details.abs + NUM_SLIDES - 1, true, animation);
    },
    animationEnded(s) {
      s.moveToIdx(s.track.details.abs + NUM_SLIDES - 1, true, animation);
    },
  });

  const handleClickPlayButton = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div className={styles.component}>
      <h2>Get in Touch</h2>
      <div className={styles['cta-and-video-container']}>
        <div className={styles['cta-container']}>
          <div className={styles['cta-text']}>
            <p>Need more information? Get in touch with an expert at eXp Commercial today.</p>
          </div>
          <div className={styles['cta-buttons']}>
            <Link href="/contact-us">
              <Button theme={PRIMARY_THEME} label='Get in Touch' />
            </Link>
            <Link href="/join-us">
              <Button theme={SECONDARY_THEME} label='Join Us' />
            </Link>
          </div>
        </div>
        <div className={styles['video-container']} >
          {
            // product decided they didn't want a play button after all, sorry Alex :(
            // but we can keep this functionality here because we already made it so why not
            !autoPlay && <PlayButton isPlaying={isPlaying} onClick={handleClickPlayButton} />
          }
          <video 
            autoPlay={autoPlay} 
            ref={videoRef} 
            playsInline 
            muted 
            controls={false} 
            loop={true} 
            preload="none" 
            poster="next/assets/videos/commercial/commercial-get-in-touch-poster.webp">
            <source src={'next/assets/videos/commercial/commercial-get-in-touch.webm'} type="video/webm" />
            <source src={'next/assets/videos/commercial/commercial-get-in-touch.mp4'} type="video/mp4" />
            Your browser doesn&apos;t support HTML video.
          </video>
        </div>
      </div>
      <div ref={sliderRef} className={buildClassName(styles['slider'], 'keen-slider')}>
        {Array.from({ length: NUM_SLIDES }).map((_, index) => (
          <div key={index} className={buildClassName(styles['slide'], 'keen-slider__slide')}>
            <div>Get in Touch</div>
            <CallMadeIcon />
          </div>
        ))}
      </div>
    </div>
  );
}
